/**
 * Admin — Provider Benefits CMS
 * Sprint: TREATX-ADMIN-CMS-HYGIENE-01 / Task 1
 *
 * SSOT: Talks ONLY to the existing `/api/admin/site-content/{key}` endpoints
 * served by `site_content_routes` + `site_content_service`. NO new backend.
 * NO new CMS framework. NO new database collection.
 *
 * Manages 3 keys (already seeded by per-key idempotent seeder):
 *   - provider_benefits_hero (1 block — headline / body / CTA)
 *   - provider_benefits_rows (N blocks — each benefit row)
 *   - provider_benefits_cta  (1 block — final CTA band)
 *
 * Each block exposes the InfoBlockItem shape:
 *   { headline, body_md, cta_text?, cta_url?, sort_order?, image_url?, active? }
 *
 * Save flow: load latest payload -> edit locally -> POST whole payload back.
 * The backend service creates a new version + deactivates prior versions
 * (versioning is SSOT — every save is an auditable revision).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown,
  Eye, EyeOff, ExternalLink, Sparkles, Megaphone, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { MediaPicker } from '../../components/cms/MediaPicker';
import { MultilingualInput } from '../../components/MultilingualInput';

const KEYS = [
  {
    id: 'provider_benefits_hero',
    label: 'Hero',
    icon: Sparkles,
    color: 'bg-blue-100 text-blue-700',
    description: 'Top of page — single headline / subtitle / primary CTA.',
    maxBlocks: 1,
  },
  {
    id: 'provider_benefits_rows',
    label: 'Benefit Rows',
    icon: Layers,
    color: 'bg-indigo-100 text-indigo-700',
    description: 'Body — N benefit rows. Each row is independently editable.',
    maxBlocks: 12,
  },
  {
    id: 'provider_benefits_cta',
    label: 'Final CTA',
    icon: Megaphone,
    color: 'bg-amber-100 text-amber-700',
    description: 'Bottom band — single closing headline / body / CTA button.',
    maxBlocks: 1,
  },
  {
    id: 'provider_benefits_showcase',
    label: 'Capability Showcase (4 blocks)',
    icon: Layers,
    color: 'bg-emerald-100 text-emerald-700',
    description: 'End of page — four capability blocks. Saving STAGES a version; it reaches the public page only after you approve it.',
    maxBlocks: 4,
    requiresApproval: true,
  },
];

const EMPTY_BLOCK = {
  headline: '',
  body_md: '',
  cta_text: '',
  cta_url: '',
  sort_order: 0,
  image_url: '',
  image_url_tr: '',
  // Stage 2 — accessibility + caption, stored on the SAME site-content
  // payload (no new authority) and rendered by the public page.
  image_alt: '',
  image_alt_tr: '',
  image_caption: '',
  image_caption_tr: '',
  active: true,
};

const fetchKey = async (key) => {
  const res = await api.get(`/admin/site-content/${key}`);
  // Admin GET returns either {content: {...}} or {content: null, key_info: {...}}
  const content = res.data?.content;
  return {
    title: content?.title || '',
    version: content?.version || 0,
    updated_at: content?.updated_at || null,
    updated_by: content?.updated_by || null,
    blocks: Array.isArray(content?.payload?.blocks) ? content.payload.blocks : [],
  };
};

// PB-I18N-01 — copy fields accept EITHER a plain string (every pre-existing
// record) OR the canonical multilingual map {en, tr, ...} that
// `landing_content_blocks` already uses. `site_content.payload` is schema-free
// (`Dict[str, Any]`), so this needs no migration and no backend change: an old
// single-language record is simply a string and keeps rendering unchanged.
// Empty-only maps collapse back to null so a blank field stays blank.
const trimCopy = (v, { nullable = false } = {}) => {
  if (v && typeof v === 'object') {
    const out = {};
    for (const [lang, text] of Object.entries(v)) {
      const s = typeof text === 'string' ? text.trim() : '';
      if (s) out[lang] = s;
    }
    if (Object.keys(out).length) return out;
    return nullable ? null : '';
  }
  const s = (v || '').trim();
  return nullable ? (s || null) : s;
};

// PB-I18N-02 — mandatory-copy check, deliberately defined in terms of
// `trimCopy` so validation and normalisation can never disagree: a field counts
// as filled exactly when normalisation would keep something. That covers a
// legacy plain string, a multilingual map with at least one non-empty language,
// and rejects whitespace-only input in either shape.
//
// This replaces `!b.headline?.trim()`, which threw
// `TypeError: b.headline.trim is not a function` once the field held a map —
// optional chaining guards a null `headline`, not a missing `.trim`. It threw
// from inside `findIndex`, i.e. BEFORE the save try/catch, so nothing was sent
// and no error toast appeared.
const hasCopy = (v) => trimCopy(v, { nullable: true }) !== null;

const saveKey = async (key, title, blocks, { stage = false } = {}) => {
  // Normalize blocks (sort_order auto-numbered to ensure deterministic order)
  const normalized = (blocks || []).map((b, idx) => ({
    headline: trimCopy(b.headline),
    body_md: trimCopy(b.body_md),
    cta_text: trimCopy(b.cta_text, { nullable: true }),
    cta_url: (b.cta_url || '').trim() || null,
    sort_order: typeof b.sort_order === 'number' ? b.sort_order : idx + 1,
    image_url: (b.image_url || '').trim() || null,
    image_url_tr: (b.image_url_tr || '').trim() || null,
    image_alt: (b.image_alt || '').trim() || null,
    image_alt_tr: (b.image_alt_tr || '').trim() || null,
    image_caption: (b.image_caption || '').trim() || null,
    image_caption_tr: (b.image_caption_tr || '').trim() || null,
    active: b.active !== false,
  }));
  const res = await api.post(`/admin/site-content/${key}${stage ? '?activate=false' : ''}`, {
    title: title || null,
    payload: { blocks: normalized },
  });
  return res.data;
};

// TREATX-PROVIDER-SHOWCASE-01 — approval-controlled keys stage a version and
// publish it only when a Super Admin activates it. Both calls are the EXISTING
// site-content endpoints; no second approval system.
const fetchVersions = async (key) => {
  const res = await api.get(`/admin/site-content/${key}/history`);
  return res.data?.versions || [];
};

const activateVersion = async (key, version) => {
  const res = await api.post(`/admin/site-content/${key}/activate/${version}`);
  return res.data;
};

// ─────────────────────────────────────────────────────────────
// Single block editor card
// ─────────────────────────────────────────────────────────────
const BlockEditor = ({ block, index, total, onChange, onRemove, onMove }) => {
  const set = (k, v) => onChange({ ...block, [k]: v });
  return (
    <div
      className={`border rounded-xl p-4 bg-white shadow-sm ${block.active === false ? 'opacity-60' : ''}`}
      data-testid={`pb-block-${index}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">#{index + 1}</Badge>
          {block.active === false && <Badge className="bg-slate-200 text-slate-700">Hidden</Badge>}
          <span className="text-xs text-slate-400">sort_order: {block.sort_order ?? index + 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onMove(index, -1)} disabled={index === 0} data-testid={`pb-move-up-${index}`}>
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onMove(index, 1)} disabled={index === total - 1} data-testid={`pb-move-down-${index}`}>
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => set('active', !(block.active !== false))}
            title={block.active === false ? 'Show on page' : 'Hide from page'}
            data-testid={`pb-toggle-active-${index}`}
          >
            {block.active === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="text-rose-600 hover:bg-rose-50" data-testid={`pb-remove-${index}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2" data-testid={`pb-headline-${index}`}>
          <MultilingualInput
            label="Headline"
            value={block.headline}
            onChange={(v) => set('headline', v)}
            placeholder="e.g. Reach New International Patients"
          />
        </div>
        <div className="md:col-span-2" data-testid={`pb-body-${index}`}>
          <MultilingualInput
            label="Body (markdown allowed)"
            value={block.body_md}
            onChange={(v) => set('body_md', v)}
            placeholder="Short benefit-oriented description shown beneath the headline."
            multiline
          />
        </div>
        <div data-testid={`pb-cta-text-${index}`}>
          <MultilingualInput
            label="CTA Text (optional)"
            value={block.cta_text}
            onChange={(v) => set('cta_text', v)}
            placeholder="e.g. Become a Provider"
            policy="translatable_optional"
          />
        </div>
        <div>
          <Label className="text-xs">CTA URL (optional)</Label>
          <Input
            value={block.cta_url || ''}
            onChange={(e) => set('cta_url', e.target.value)}
            placeholder="/provider/register"
            data-testid={`pb-cta-url-${index}`}
          />
        </div>
        <div className="md:col-span-2">
          <MediaPicker
            label="Image — GLOBAL / English interface (optional)"
            hint="Upload from your device or choose an image you already uploaded. Leave empty to use the default decorative tile."
            value={block.image_url || ''}
            onChange={(v) => set('image_url', v)}
            altText={block.image_alt || ''}
            onAltTextChange={(v) => set('image_alt', v)}
            caption={block.image_caption || ''}
            onCaptionChange={(v) => set('image_caption', v)}
            testId={`pb-image-${index}`}
          />
        </div>
        <div className="md:col-span-2">
          <MediaPicker
            label="Image — TR / Turkish interface (optional)"
            hint="Turkish-interface screenshot of the same screen. Shown only on the TR surface."
            value={block.image_url_tr || ''}
            onChange={(v) => set('image_url_tr', v)}
            altText={block.image_alt_tr || ''}
            onAltTextChange={(v) => set('image_alt_tr', v)}
            caption={block.image_caption_tr || ''}
            onCaptionChange={(v) => set('image_caption_tr', v)}
            testId={`pb-image-tr-${index}`}
          />
          <p className="mt-1 text-[11px] text-slate-500">
            The canonical surface resolver decides which variant a visitor receives — never the
            browser language. A TR visitor with no Turkish asset sees the row without an image
            rather than the English screen.
          </p>
        </div>
        <div>
          <Label className="text-xs">Sort Order</Label>
          <Input
            type="number"
            value={block.sort_order ?? index + 1}
            onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)}
            data-testid={`pb-sort-order-${index}`}
          />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Per-key section (Hero / Rows / CTA)
// ─────────────────────────────────────────────────────────────
const KeySection = ({ keyMeta }) => {
  const [state, setState] = useState({ title: '', blocks: [], version: 0, updated_at: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Approval-controlled keys only: staged versions awaiting Super Admin approval.
  const [versions, setVersions] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchKey(keyMeta.id);
      setState({
        title: data.title,
        blocks: data.blocks.length ? data.blocks : [{ ...EMPTY_BLOCK, sort_order: 1 }],
        version: data.version,
        updated_at: data.updated_at,
        updated_by: data.updated_by,
      });
      if (keyMeta.requiresApproval) {
        try {
          const vs = await fetchVersions(keyMeta.id);
          setVersions(vs);
          // Edit the LATEST version (staged or active) — the staged draft is
          // what the next approval publishes. The live page keeps serving the
          // active version until then.
          const latest = vs[0];
          if (latest?.payload?.blocks?.length) {
            setState({
              title: latest.title,
              blocks: latest.payload.blocks,
              version: latest.version,
              updated_at: latest.updated_at || latest.created_at,
              updated_by: latest.updated_by,
            });
          }
        } catch (_e) {
          setVersions([]);
        }
      }
    } catch (e) {
      toast.error(`Load failed: ${keyMeta.label}`);
      setState({ title: '', blocks: [{ ...EMPTY_BLOCK, sort_order: 1 }], version: 0, updated_at: null });
    } finally {
      setLoading(false);
    }
  }, [keyMeta.id, keyMeta.label, keyMeta.requiresApproval]);

  useEffect(() => { reload(); }, [reload]);

  const updateBlock = (idx, next) => {
    setState((s) => ({ ...s, blocks: s.blocks.map((b, i) => (i === idx ? next : b)) }));
  };
  const removeBlock = (idx) => {
    setState((s) => ({ ...s, blocks: s.blocks.filter((_, i) => i !== idx) }));
  };
  const moveBlock = (idx, delta) => {
    setState((s) => {
      const arr = [...s.blocks];
      const j = idx + delta;
      if (j < 0 || j >= arr.length) return s;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...s, blocks: arr.map((b, i) => ({ ...b, sort_order: i + 1 })) };
    });
  };
  const addBlock = () => {
    setState((s) => {
      if (s.blocks.length >= keyMeta.maxBlocks) {
        toast.warning(`Max ${keyMeta.maxBlocks} block(s) for this section`);
        return s;
      }
      return { ...s, blocks: [...s.blocks, { ...EMPTY_BLOCK, sort_order: s.blocks.length + 1 }] };
    });
  };

  const save = async () => {
    // Validate
    const invalid = state.blocks.findIndex((b) => !hasCopy(b.headline) || !hasCopy(b.body_md));
    if (invalid !== -1) {
      toast.error(`Block #${invalid + 1}: headline and body are required`);
      return;
    }
    setSaving(true);
    try {
      const res = await saveKey(keyMeta.id, state.title, state.blocks,
        { stage: !!keyMeta.requiresApproval });
      const v = res?.content?.version ?? '?';
      toast.success(keyMeta.requiresApproval
        ? `Staged ${keyMeta.label} → v${v}. Approve it to publish.`
        : `Saved ${keyMeta.label} → v${v}`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const approve = async (version) => {
    setSaving(true);
    try {
      await activateVersion(keyMeta.id, version);
      toast.success(`Approved v${version} — now live on /provider-benefits`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Approval failed');
    } finally {
      setSaving(false);
    }
  };

  const Icon = keyMeta.icon;
  return (
    <Card data-testid={`pb-section-${keyMeta.id}`} className="border border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${keyMeta.color}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div>
              <CardTitle className="text-base">{keyMeta.label}</CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">{keyMeta.description}</p>
            </div>
          </div>
          <div className="text-xs text-slate-400 flex items-center gap-3">
            <span>v{state.version || '—'}</span>
            {state.updated_at && (
              <span>updated {formatDateTimeLocale(state.updated_at)}</span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <>
            {keyMeta.requiresApproval && (
              <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3"
                   data-testid={`pb-approval-${keyMeta.id}`}>
                <p className="text-xs font-medium text-emerald-900">
                  Approval required — saving stages a new version. The public page keeps
                  showing the currently approved version until you approve the new one.
                </p>
                <div className="mt-2 flex flex-col gap-1">
                  {versions.length === 0 && (
                    <span className="text-xs text-slate-500">No versions yet.</span>
                  )}
                  {versions.slice(0, 5).map((v) => (
                    <div key={v.version} className="flex items-center gap-2 flex-wrap text-xs"
                         data-testid={`pb-version-${keyMeta.id}-${v.version}`}>
                      <Badge className={v.is_active ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-700'}>
                        v{v.version} {v.is_active ? 'LIVE' : 'staged'}
                      </Badge>
                      <span className="text-slate-500">
                        {formatDateTimeLocale(v.updated_at || v.created_at)}
                      </span>
                      {!v.is_active && (
                        <Button
                          size="sm" variant="outline" disabled={saving}
                          onClick={() => approve(v.version)}
                          data-testid={`pb-approve-${keyMeta.id}-${v.version}`}
                        >
                          Approve &amp; publish v{v.version}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-4">
              <Label className="text-xs">Section title (internal — for admin reference)</Label>              <Input
                value={state.title || ''}
                onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
                placeholder={keyMeta.label}
                data-testid={`pb-title-${keyMeta.id}`}
              />
            </div>

            <div className="space-y-3">
              {state.blocks.map((b, idx) => (
                <BlockEditor
                  key={`${keyMeta.id}-${idx}`}
                  block={b}
                  index={idx}
                  total={state.blocks.length}
                  onChange={(next) => updateBlock(idx, next)}
                  onRemove={removeBlock}
                  onMove={moveBlock}
                />
              ))}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={addBlock}
                disabled={state.blocks.length >= keyMeta.maxBlocks}
                data-testid={`pb-add-block-${keyMeta.id}`}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add block
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <span
                  className="hidden sm:inline text-xs text-amber-700"
                  data-testid={`pb-live-hint-${keyMeta.id}`}
                >
                  Saving publishes immediately
                </span>
                <Button variant="ghost" size="sm" onClick={reload} disabled={saving} data-testid={`pb-revert-${keyMeta.id}`}>
                  Revert
                </Button>
                <Button onClick={save} disabled={saving} data-testid={`pb-save-${keyMeta.id}`}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save &amp; publish
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
const AdminProviderBenefitsCMS = ({ embedded = false }) => {
  const navigate = useNavigate();

  // Rendered inside Website Management → Provider Benefits: the shell already
  // supplies the page chrome, so only the editable sections are emitted.
  if (embedded) {
    return (
      <div className="space-y-6" data-testid="admin-provider-benefits-cms">
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
           data-testid="pb-live-notice">
          Saving here updates the live <strong>/provider-benefits</strong> page immediately. Each
          save is kept as an audited version you can restore.
        </p>
        {KEYS.map((k) => (
          <KeySection key={k.id} keyMeta={k} />
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-provider-benefits-cms">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/dashboard')} data-testid="pb-back">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Provider Benefits CMS</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Edits go live immediately on <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">/provider-benefits</code>. Each save creates a new audited version.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => window.open('/#/provider-benefits', '_blank')} data-testid="pb-preview">
            <ExternalLink className="h-4 w-4 mr-2" />
            Preview page
          </Button>
        </div>

        <div className="space-y-6">
          {KEYS.map((k) => (
            <KeySection key={k.id} keyMeta={k} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminProviderBenefitsCMS;
